import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.bubble.css";
import "./work-journal.css";
import "../../utils/PromangRichTextEditor.css";
import { TWclassNames } from "../../utils/Div";
import { WorkJournalImagePopover } from "./WorkJournalImagePopover";
import { DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU } from "../../utils/richTextEditorContextMenuDefaults";

/** @typedef {{ index: number, length: number }} QuillRangeLike */

const Block = Quill.import("blots/block");
const Container = Quill.import("blots/container");

let tableFormatsRegistered = false;

function registerTableFormats() {
  if (tableFormatsRegistered) return;

  class TableCell extends Block {}
  TableCell.blotName = "table-cell";
  TableCell.tagName = "TD";

  class TableHeaderCell extends Block {}
  TableHeaderCell.blotName = "table-header-cell";
  TableHeaderCell.tagName = "TH";

  class TableRow extends Container {}
  TableRow.blotName = "table-row";
  TableRow.tagName = "TR";
  TableRow.allowedChildren = [TableCell, TableHeaderCell];

  class TableBody extends Container {}
  TableBody.blotName = "table-body";
  TableBody.tagName = "TBODY";
  TableBody.allowedChildren = [TableRow];

  class Table extends Container {}
  Table.blotName = "table";
  Table.tagName = "TABLE";
  Table.allowedChildren = [TableBody, TableRow];

  Quill.register(
    {
      "formats/table": Table,
      "formats/table-body": TableBody,
      "formats/table-row": TableRow,
      "formats/table-cell": TableCell,
      "formats/table-header-cell": TableHeaderCell,
    },
    true
  );

  tableFormatsRegistered = true;
}

registerTableFormats();

const DEFAULT_CONTEXT_MENU = {
  ...DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU,
  onMoveSelectionToPage: undefined,
  onCreatePageWithSelection: undefined,
};

/**
 * Snapshot of the current DOM selection inside `root` for HTML extraction (Quill 1.x).
 * @param {HTMLElement} root
 * @returns {{ html: string, text: string }}
 */
function getDomSelectionSnapshot(root) {
  let html = "";
  let text = "";
  try {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !root) return { html, text };
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return { html, text };
    text = sel.toString() || "";
    const frag = range.cloneContents();
    const div = document.createElement("div");
    div.appendChild(frag);
    html = div.innerHTML;
  } catch {
    /* ignore */
  }
  return { html, text };
}

/**
 * @param {*} quill — Quill instance from ReactQuill
 * @param {QuillRangeLike | null} savedRange
 */
function buildSelectionContextPayload(quill, savedRange) {
  if (!savedRange || savedRange.length <= 0) {
    return {
      quill,
      range: null,
      html: "",
      text: "",
      hasSelection: false,
    };
  }
  const plain = quill.getText(savedRange.index, savedRange.length) || "";
  const { html, text } = getDomSelectionSnapshot(quill.root);
  return {
    quill,
    range: { index: savedRange.index, length: savedRange.length },
    html: html || "",
    text: text || plain,
    hasSelection: true,
  };
}

const BUBBLE_TOOLBAR = [
  ["bold", "italic", "underline", "strike"],
  ["blockquote", "code-block"],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ indent: "-1" }, { indent: "+1" }],
  [{ header: [1, 2, 3, false] }],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ["link", "image"],
  ["clean"],
];

const FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "alt",
  "width",
  "height",
  "color",
  "background",
  "align",
  "table",
  "table-body",
  "table-row",
  "table-cell",
  "table-header-cell",
];

const TOOLTIP_VIEWPORT_PADDING = 12;

function clampNumber(value, min, max) {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

const buildTableHtml = (headerLabel, rows = 3, columns = 3) => {
  const headerCells = Array.from({ length: columns }, (_, index) => (
    `<th>${headerLabel} ${index + 1}</th>`
  )).join("");
  const bodyRows = Array.from({ length: Math.max(rows - 1, 1) }, () => {
    const cells = Array.from({ length: columns }, () => "<td><br></td>").join("");
    return `<tr>${cells}</tr>`;
  }).join("");

  return `<table><tbody><tr>${headerCells}</tr>${bodyRows}</tbody></table><p><br></p>`;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const isSafeClipboardUrl = (value) => {
  const url = String(value || "").trim();
  return /^(https?:|mailto:|tel:|data:image\/|blob:|\/)/i.test(url);
};

const cleanPastedCellHtml = (cell) => {
  const clone = cell.cloneNode(true);
  clone.querySelectorAll("script, style, meta, link, colgroup").forEach((node) => node.remove());
  clone.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      if (name === "href" && node.tagName === "A") {
        if (!isSafeClipboardUrl(attribute.value)) node.removeAttribute(attribute.name);
        return;
      }
      if (
        node.tagName === "IMG" &&
        ["src", "alt", "width", "height"].includes(name)
      ) {
        if (name === "src" && !isSafeClipboardUrl(attribute.value)) {
          node.removeAttribute(attribute.name);
        }
        return;
      }
      if (name === "colspan" || name === "rowspan") return;
      node.removeAttribute(attribute.name);
    });
  });
  return clone.innerHTML.trim() || "<br>";
};

const tableElementToHtml = (table) => {
  const rows = Array.from(table.querySelectorAll("tr"));
  if (!rows.length) return "";

  const rowHtml = rows
    .map((row) => {
      const cells = Array.from(row.children).filter((cell) =>
        ["TD", "TH"].includes(cell.tagName)
      );
      if (!cells.length) return "";
      const cellsHtml = cells
        .map((cell) => {
          const tagName = cell.tagName === "TH" ? "th" : "td";
          const colSpan = cell.getAttribute("colspan");
          const rowSpan = cell.getAttribute("rowspan");
          const attrs = [
            colSpan && Number(colSpan) > 1 ? ` colspan="${Number(colSpan)}"` : "",
            rowSpan && Number(rowSpan) > 1 ? ` rowspan="${Number(rowSpan)}"` : "",
          ].join("");
          return `<${tagName}${attrs}>${cleanPastedCellHtml(cell)}</${tagName}>`;
        })
        .join("");
      return `<tr>${cellsHtml}</tr>`;
    })
    .filter(Boolean)
    .join("");

  return rowHtml ? `<table><tbody>${rowHtml}</tbody></table>` : "";
};

const normalizePastedTables = (html) => {
  if (!html || !/<table[\s>]/i.test(html)) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  const tables = Array.from(doc.querySelectorAll("table"))
    .map(tableElementToHtml)
    .filter(Boolean);
  return tables.length ? `${tables.join("<p><br></p>")}<p><br></p>` : "";
};

const sanitizeRichClipboardHtml = (html) => {
  if (!html) return "";
  if (!/(<table[\s>]|<a[\s>]|<img[\s>]|<h[1-6][\s>]|<ul[\s>]|<ol[\s>]|<blockquote[\s>]|mso-|StartFragment)/i.test(html)) {
    return "";
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, style, meta, link, colgroup, xml").forEach((node) => node.remove());
  doc.querySelectorAll("table").forEach((table) => {
    const normalized = tableElementToHtml(table);
    if (!normalized) {
      table.remove();
      return;
    }
    const wrapper = doc.createElement("div");
    wrapper.innerHTML = normalized;
    table.replaceWith(wrapper.firstElementChild);
  });

  const allowedTags = new Set([
    "A",
    "B",
    "BLOCKQUOTE",
    "BR",
    "CODE",
    "DIV",
    "EM",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "I",
    "IMG",
    "LI",
    "OL",
    "P",
    "PRE",
    "S",
    "SPAN",
    "STRONG",
    "TABLE",
    "TBODY",
    "TD",
    "TH",
    "TR",
    "U",
    "UL",
  ]);

  Array.from(doc.body.querySelectorAll("*")).forEach((node) => {
    if (!allowedTags.has(node.tagName)) {
      node.replaceWith(...Array.from(node.childNodes));
      return;
    }

    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      if (node.tagName === "A" && name === "href") {
        if (isSafeClipboardUrl(attribute.value)) {
          node.setAttribute("target", "_blank");
          node.setAttribute("rel", "noopener noreferrer");
        } else {
          node.removeAttribute(attribute.name);
        }
        return;
      }
      if (
        node.tagName === "IMG" &&
        ["src", "alt", "width", "height"].includes(name)
      ) {
        if (name === "src" && !isSafeClipboardUrl(attribute.value)) {
          node.removeAttribute(attribute.name);
        }
        return;
      }
      if (["TD", "TH"].includes(node.tagName) && (name === "colspan" || name === "rowspan")) {
        return;
      }
      node.removeAttribute(attribute.name);
    });
  });

  const result = doc.body.innerHTML.trim();
  return result ? `${result}<p><br></p>` : "";
};

const tabSeparatedTextToTableHtml = (text) => {
  if (!text || !text.includes("\t")) return "";
  const rows = text
    .trim()
    .split(/\r?\n/)
    .map((row) => row.split("\t"));
  if (!rows.length || rows.every((row) => row.length <= 1)) return "";

  const rowHtml = rows
    .map((row) => {
      const cells = row.map((cell) => `<td>${escapeHtml(cell) || "<br>"}</td>`).join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  return `<table><tbody>${rowHtml}</tbody></table><p><br></p>`;
};

const splitMarkdownTableRow = (line) => {
  const trimmed = String(line || "").trim();
  if (!trimmed.includes("|")) return [];
  const withoutEdges = trimmed.replace(/^\|/, "").replace(/\|$/, "");
  return withoutEdges.split("|").map((cell) => cell.trim());
};

const isMarkdownTableSeparator = (cells) =>
  cells.length > 1 &&
  cells.every((cell) => /^:?-{3,}:?$/.test(String(cell || "").trim()));

const markdownTableTextToTableHtml = (text) => {
  if (!text || !text.includes("|")) return "";
  const lines = text
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 3) return "";

  const rows = lines.map(splitMarkdownTableRow);
  const separatorIndex = rows.findIndex(isMarkdownTableSeparator);
  if (separatorIndex <= 0) return "";

  const header = rows[separatorIndex - 1];
  const bodyRows = rows.slice(separatorIndex + 1).filter((row) => row.length > 1);
  if (!header.length || !bodyRows.length) return "";

  const columnCount = Math.max(header.length, ...bodyRows.map((row) => row.length));
  const normalizeRow = (row) =>
    Array.from({ length: columnCount }, (_, index) => row[index] || "");

  const headerHtml = normalizeRow(header)
    .map((cell) => `<th>${escapeHtml(cell) || "<br>"}</th>`)
    .join("");
  const bodyHtml = bodyRows
    .map((row) => {
      const cellsHtml = normalizeRow(row)
        .map((cell) => `<td>${escapeHtml(cell) || "<br>"}</td>`)
        .join("");
      return `<tr>${cellsHtml}</tr>`;
    })
    .join("");

  return `<table><tbody><tr>${headerHtml}</tr>${bodyHtml}</tbody></table><p><br></p>`;
};

const renderMarkdownInline = (text) => {
  const tokens = [];
  const addToken = (html) => {
    const token = `\uE000${tokens.length}\uE001`;
    tokens.push({ token, html });
    return token;
  };

  let value = String(text || "");
  value = value.replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, (match, alt, url) => {
    if (!isSafeClipboardUrl(url)) return match;
    return addToken(`<img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}" />`);
  });
  value = value.replace(/\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, (match, label, url) => {
    if (!isSafeClipboardUrl(url)) return match;
    return addToken(`<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`);
  });
  value = value.replace(/`([^`]+)`/g, (_match, code) => addToken(`<code>${escapeHtml(code)}</code>`));
  value = value.replace(/\*\*([^*]+)\*\*/g, (_match, strong) => addToken(`<strong>${escapeHtml(strong)}</strong>`));
  value = value.replace(/__([^_]+)__/g, (_match, strong) => addToken(`<strong>${escapeHtml(strong)}</strong>`));
  value = value.replace(/(^|[^*])\*([^*\n]+)\*/g, (_match, prefix, em) => `${prefix}${addToken(`<em>${escapeHtml(em)}</em>`)}`);
  value = value.replace(/(^|[^_])_([^_\n]+)_/g, (_match, prefix, em) => `${prefix}${addToken(`<em>${escapeHtml(em)}</em>`)}`);
  value = value.replace(/(https?:\/\/[^\s<]+)/g, (url) => addToken(`<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>`));

  let escaped = escapeHtml(value);
  tokens.forEach(({ token, html }) => {
    escaped = escaped.split(token).join(html);
  });
  return escaped;
};

const isLikelyMarkdownRichText = (text) =>
  Boolean(
    text &&
      (/^\s{0,3}#{1,6}\s+/m.test(text) ||
        /^\s{0,3}([-*+]\s+|\d+\.\s+)/m.test(text) ||
        /^\s{0,3}>\s+/m.test(text) ||
        /\|.+\|\s*\n\s*\|?\s*:?-{3,}:?\s*\|/m.test(text) ||
        /!\[[^\]]*\]\([^)]+\)/.test(text) ||
        /\[[^\]]+\]\([^)]+\)/.test(text) ||
        /\*\*[^*]+\*\*/.test(text) ||
        /https?:\/\/\S+/.test(text))
  );

const markdownTextToRichHtml = (text) => {
  if (!isLikelyMarkdownRichText(text)) return "";

  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  const collectList = (ordered) => {
    const items = [];
    const pattern = ordered ? /^\s{0,3}\d+\.\s+(.+)$/ : /^\s{0,3}[-*+]\s+(.+)$/;
    while (i < lines.length) {
      const match = lines[i].match(pattern);
      if (!match) break;
      items.push(`<li>${renderMarkdownInline(match[1])}</li>`);
      i += 1;
    }
    const tagName = ordered ? "ol" : "ul";
    blocks.push(`<${tagName}>${items.join("")}</${tagName}>`);
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    const currentRow = splitMarkdownTableRow(line);
    const nextRow = splitMarkdownTableRow(lines[i + 1] || "");
    if (currentRow.length > 1 && isMarkdownTableSeparator(nextRow)) {
      const tableLines = [line, lines[i + 1]];
      i += 2;
      while (i < lines.length && splitMarkdownTableRow(lines[i]).length > 1) {
        tableLines.push(lines[i]);
        i += 1;
      }
      blocks.push(markdownTableTextToTableHtml(tableLines.join("\n")).replace(/<p><br><\/p>$/, ""));
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const level = Math.min(heading[1].length, 3);
      blocks.push(`<h${level}>${renderMarkdownInline(heading[2])}</h${level}>`);
      i += 1;
      continue;
    }

    if (/^\s{0,3}>\s+/.test(line)) {
      const quoteLines = [];
      while (i < lines.length && /^\s{0,3}>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^\s{0,3}>\s?/, ""));
        i += 1;
      }
      blocks.push(`<blockquote>${quoteLines.map(renderMarkdownInline).join("<br>")}</blockquote>`);
      continue;
    }

    if (/^\s{0,3}\d+\.\s+/.test(line)) {
      collectList(true);
      continue;
    }

    if (/^\s{0,3}[-*+]\s+/.test(line)) {
      collectList(false);
      continue;
    }

    const paragraphLines = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^\s{0,3}(#{1,6}\s+|>\s+|\d+\.\s+|[-*+]\s+)/.test(lines[i]) &&
      !(splitMarkdownTableRow(lines[i]).length > 1 && isMarkdownTableSeparator(splitMarkdownTableRow(lines[i + 1] || "")))
    ) {
      paragraphLines.push(lines[i]);
      i += 1;
    }
    blocks.push(`<p>${paragraphLines.map((paragraphLine) => renderMarkdownInline(paragraphLine.trim())).join("<br>")}</p>`);
  }

  return blocks.length ? `${blocks.join("")}<p><br></p>` : "";
};

const pasteHtmlAndMoveCaretToEnd = (quill, index, html, onChange) => {
  if (!quill || !html) return;
  const beforeLength = quill.getLength();
  quill.clipboard.dangerouslyPasteHTML(index, html, "user");
  const afterLength = quill.getLength();
  const insertedLength = Math.max(0, afterLength - beforeLength);
  const nextIndex = Math.min(
    Math.max(index + insertedLength, index),
    Math.max(0, afterLength - 1)
  );
  quill.setSelection(nextIndex, 0, "silent");
  quill.focus();
  if (quill.root) onChange(quill.root.innerHTML);
};

/**
 * Notion-style rich text: bubble theme shows formatting when text is selected.
 * clipboard.matchVisual: false improves HTML paste fidelity from browsers / OneNote / web.
 *
 * @param {object} [props.contextMenu] — `false` disables the custom menu (browser default).
 *   Otherwise merged with defaults: `insertImage`, `moveSelectionToPage`, `createPageWithSelection`,
 *   and optional `onMoveSelectionToPage` / `onCreatePageWithSelection` callbacks
 *   (receives `{ quill, range, html, text, hasSelection }`).
 */
export function WorkJournalRichEditor({
  value,
  onChange,
  placeholder,
  fullscreen,
  className,
  contextMenu: contextMenuProp,
  tablesEnabled = false,
}) {
  const { t } = useTranslation();
  const quillRef = useRef(null);
  const editorShellRef = useRef(null);
  const [imagePopover, setImagePopover] = useState(null);
  const contextMenuRangeRef = useRef(null);
  const pendingImageInsertRangeRef = useRef(null);
  const fileInputRef = useRef(null);
  const contextMenuRef = useRef(null);
  const [contextMenuUi, setContextMenuUi] = useState(null);

  const contextMenu = useMemo(() => {
    if (contextMenuProp === false) return null;
    return { ...DEFAULT_CONTEXT_MENU, ...(contextMenuProp || {}) };
  }, [contextMenuProp]);

  const modules = useMemo(
    () => ({
      toolbar: BUBBLE_TOOLBAR,
      clipboard: {
        matchVisual: false,
      },
      history: {
        delay: 800,
        maxStack: 200,
        userOnly: true,
      },
    }),
    []
  );

  // Screenshots / copied image files: Quill does not handle image/* clipboard items by default.
  useEffect(() => {
    let removeListener;
    let intervalId;
    let attempts = 0;
    const maxAttempts = 40;

    const tryBind = () => {
      const quill = quillRef.current?.getEditor?.();
      const root = quill?.root;
      if (!root) return false;

      const onPaste = (e) => {
        const html = e.clipboardData?.getData?.("text/html");
        const plainText = e.clipboardData?.getData?.("text/plain");
        const normalizedRichHtml =
          sanitizeRichClipboardHtml(html) ||
          normalizePastedTables(html) ||
          tabSeparatedTextToTableHtml(plainText) ||
          markdownTextToRichHtml(plainText);
        if (normalizedRichHtml) {
          e.preventDefault();
          e.stopPropagation();
          const q = quillRef.current?.getEditor?.();
          if (!q) return;
          const range = q.getSelection(true);
          const index =
            range != null ? range.index + range.length : Math.max(0, q.getLength() - 1);
          pasteHtmlAndMoveCaretToEnd(q, index, normalizedRichHtml, onChange);
          return;
        }

        const items = e.clipboardData?.items;
        if (!items?.length) return;
        for (let i = 0; i < items.length; i += 1) {
          const item = items[i];
          if (item.kind === "file" && item.type?.startsWith?.("image/")) {
            e.preventDefault();
            e.stopPropagation();
            const file = item.getAsFile();
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
              const q = quillRef.current?.getEditor?.();
              if (!q || !reader.result) return;
              const range = q.getSelection(true);
              const index =
                range != null ? range.index : Math.max(0, q.getLength() - 1);
              q.insertEmbed(index, "image", reader.result, "user");
              q.setSelection(index + 1, "silent");
            };
            reader.readAsDataURL(file);
            return;
          }
        }
      };

      root.addEventListener("paste", onPaste, true);
      removeListener = () => root.removeEventListener("paste", onPaste, true);
      return true;
    };

    intervalId = window.setInterval(() => {
      if (tryBind()) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      } else {
        attempts += 1;
        if (attempts >= maxAttempts) window.clearInterval(intervalId);
      }
    }, 32);

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      if (removeListener) removeListener();
    };
  }, [onChange]);

  // Render tooltip in viewport coordinates so ancestors with overflow:auto/hidden cannot clip it.
  useEffect(() => {
    let cleanup;
    const intervalId = window.setInterval(() => {
      const quill = quillRef.current?.getEditor?.();
      const tooltip = quill?.theme?.tooltip;
      if (!tooltip || tooltip.__journalTooltipViewportFix) return;

      tooltip.__journalTooltipViewportFix = true;
      const originalPosition = tooltip.position.bind(tooltip);
      tooltip.position = function journalTooltipViewportPosition(reference) {
        const el = this.root;
        if (!el || el.classList.contains("ql-hidden")) {
          return originalPosition(reference);
        }
        // Quill computes left/top for position:absolute vs .ql-container. If we left
        // position:fixed from a previous call, those pixel values are wrong (stuck left).
        el.style.position = "absolute";
        el.style.marginTop = "";
        const viewport = window.visualViewport;
        const viewportLeft = viewport?.offsetLeft || 0;
        const viewportTop = viewport?.offsetTop || 0;
        const viewportWidth = viewport?.width || window.innerWidth;
        const viewportHeight = viewport?.height || window.innerHeight;
        const shellRect = editorShellRef.current?.getBoundingClientRect?.();
        const minLeft = Math.max(
          viewportLeft + TOOLTIP_VIEWPORT_PADDING,
          (shellRect?.left ?? viewportLeft) + TOOLTIP_VIEWPORT_PADDING
        );
        const maxRight = Math.min(
          viewportLeft + viewportWidth - TOOLTIP_VIEWPORT_PADDING,
          (shellRect?.right ?? viewportLeft + viewportWidth) - TOOLTIP_VIEWPORT_PADDING
        );
        const availableWidth = Math.max(160, maxRight - minLeft);
        el.style.maxWidth = `${Math.floor(availableWidth)}px`;
        const result = originalPosition(reference);
        if (el.classList.contains("ql-hidden")) return result;
        const br = el.getBoundingClientRect();
        const minTop = viewportTop + TOOLTIP_VIEWPORT_PADDING;
        const maxLeft = maxRight - br.width;
        const maxTop =
          viewportTop + viewportHeight - br.height - TOOLTIP_VIEWPORT_PADDING;

        el.style.position = "fixed";
        el.style.left = `${Math.round(clampNumber(br.left, minLeft, maxLeft))}px`;
        el.style.top = `${Math.round(clampNumber(br.top, minTop, maxTop))}px`;
        return result;
      };

      cleanup = () => {
        if (tooltip.__journalTooltipViewportFix) {
          tooltip.position = originalPosition;
          delete tooltip.__journalTooltipViewportFix;
        }
      };
      window.clearInterval(intervalId);
    }, 32);

    const stop = window.setTimeout(() => window.clearInterval(intervalId), 3000);
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(stop);
      if (cleanup) cleanup();
    };
  }, []);

  const closeImagePopover = useCallback(() => setImagePopover(null), []);

  const closeContextMenu = useCallback(() => {
    setContextMenuUi(null);
    contextMenuRangeRef.current = null;
  }, []);

  useEffect(() => {
    if (!contextMenu) return undefined;
    let detach;
    const intervalId = window.setInterval(() => {
      const quill = quillRef.current?.getEditor?.();
      const rootEl = quill?.root;
      if (!rootEl) return;

      const onContextMenu = (e) => {
        const q = quillRef.current?.getEditor?.();
        if (!q) return;
        const range = q.getSelection(true);
        const hasSelection = Boolean(range && range.length > 0);
        const showInsert = contextMenu.insertImage;
        const showMove = contextMenu.moveSelectionToPage && hasSelection;
        const showCreate = contextMenu.createPageWithSelection && hasSelection;
        if (!showInsert && !showMove && !showCreate) return;

        e.preventDefault();
        e.stopPropagation();
        contextMenuRangeRef.current =
          range && range.length >= 0
            ? { index: range.index, length: range.length }
            : { index: Math.max(0, q.getLength() - 1), length: 0 };

        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const approxW = 240;
        const approxH = 160;
        const x = Math.max(8, Math.min(e.clientX, vw - approxW - 8));
        const y = Math.max(8, Math.min(e.clientY, vh - approxH - 8));

        setContextMenuUi({
          x,
          y,
          hasSelection,
        });
      };

      rootEl.addEventListener("contextmenu", onContextMenu);
      detach = () => rootEl.removeEventListener("contextmenu", onContextMenu);
      window.clearInterval(intervalId);
    }, 40);
    const tmax = window.setTimeout(() => window.clearInterval(intervalId), 3000);
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(tmax);
      if (detach) detach();
    };
  }, [contextMenu]);

  useEffect(() => {
    if (!contextMenuUi) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeContextMenu();
    };
    const onPointerDown = (e) => {
      const menuEl = contextMenuRef.current;
      if (menuEl && menuEl.contains(e.target)) return;
      closeContextMenu();
    };
    const onScroll = () => closeContextMenu();
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("mousedown", onPointerDown, true);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("mousedown", onPointerDown, true);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [contextMenuUi, closeContextMenu]);

  const handleInsertImagePick = useCallback(() => {
    fileInputRef.current?.click?.();
  }, []);

  const onImageFileChange = useCallback(
    (e) => {
      const file = e.target?.files?.[0];
      e.target.value = "";
      if (!file || !file.type?.startsWith?.("image/")) {
        pendingImageInsertRangeRef.current = null;
        return;
      }
      const quill = quillRef.current?.getEditor?.();
      if (!quill) {
        pendingImageInsertRangeRef.current = null;
        return;
      }
      const saved =
        pendingImageInsertRangeRef.current ?? contextMenuRangeRef.current;
      pendingImageInsertRangeRef.current = null;
      const index =
        saved && saved.length >= 0 ? saved.index : Math.max(0, quill.getLength() - 1);
      const reader = new FileReader();
      reader.onload = () => {
        const q = quillRef.current?.getEditor?.();
        if (!q || !reader.result) return;
        q.focus();
        q.insertEmbed(index, "image", reader.result, "user");
        q.setSelection(index + 1, 0, "silent");
        if (q.root) onChange(q.root.innerHTML);
      };
      reader.readAsDataURL(file);
    },
    [onChange]
  );

  const insertTable = useCallback(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    quill.focus();
    const range = quill.getSelection(true);
    const index = range ? range.index + range.length : Math.max(0, quill.getLength() - 1);
    pasteHtmlAndMoveCaretToEnd(
      quill,
      index,
      buildTableHtml(t("richTextEditor.table.defaultHeader")),
      onChange
    );
  }, [onChange, t]);

  useEffect(() => {
    let detach;
    const intervalId = window.setInterval(() => {
      const quill = quillRef.current?.getEditor?.();
      const rootEl = quill?.root;
      if (!rootEl) return;
      const onClick = (e) => {
        const node = e.target;
        if (!node || node.tagName !== "IMG") return;
        if (!rootEl.contains(node)) return;
        const q = quillRef.current?.getEditor?.();
        if (!q) return;
        q.theme?.tooltip?.hide?.();
        setImagePopover({
          img: node,
          pos: { top: e.clientY + 6, left: e.clientX + 6 },
          quill: q,
        });
      };
      rootEl.addEventListener("click", onClick);
      detach = () => rootEl.removeEventListener("click", onClick);
      window.clearInterval(intervalId);
    }, 40);
    const tmax = window.setTimeout(() => window.clearInterval(intervalId), 3000);
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(tmax);
      if (detach) detach();
    };
  }, []);

  const renderContextMenuPortal = () => {
    if (!contextMenuUi || !contextMenu || typeof document === "undefined") return null;
    const cm = contextMenu;
    const { hasSelection } = contextMenuUi;
    const showMove = cm.moveSelectionToPage && hasSelection;
    const showCreate = cm.createPageWithSelection && hasSelection;
    const moveReady = Boolean(cm.onMoveSelectionToPage);
    const createReady = Boolean(cm.onCreatePageWithSelection);

    const runMove = () => {
      const q = quillRef.current?.getEditor?.();
      if (!q) return;
      const saved = contextMenuRangeRef.current
        ? { ...contextMenuRangeRef.current }
        : null;
      cm.onMoveSelectionToPage?.(buildSelectionContextPayload(q, saved));
      closeContextMenu();
    };

    const runCreate = () => {
      const q = quillRef.current?.getEditor?.();
      if (!q) return;
      const saved = contextMenuRangeRef.current
        ? { ...contextMenuRangeRef.current }
        : null;
      cm.onCreatePageWithSelection?.(buildSelectionContextPayload(q, saved));
      closeContextMenu();
    };

    return createPortal(
      <div
        ref={contextMenuRef}
        className="promang-rich-text-context-menu"
        style={{ left: contextMenuUi.x, top: contextMenuUi.y }}
        role="menu"
        aria-label={t("richTextEditor.contextMenu.ariaLabel")}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {cm.insertImage ? (
          <button
            type="button"
            role="menuitem"
            className="promang-rich-text-context-menu__item"
            onClick={() => {
              pendingImageInsertRangeRef.current = contextMenuRangeRef.current
                ? { ...contextMenuRangeRef.current }
                : null;
              handleInsertImagePick();
              closeContextMenu();
            }}
          >
            {t("richTextEditor.contextMenu.insertImage")}
          </button>
        ) : null}
        {cm.insertImage && (showMove || showCreate) ? (
          <div className="promang-rich-text-context-menu__sep" aria-hidden />
        ) : null}
        {showMove ? (
          <button
            type="button"
            role="menuitem"
            className="promang-rich-text-context-menu__item"
            disabled={!moveReady}
            title={
              moveReady ? undefined : t("richTextEditor.contextMenu.handlerMissing")
            }
            onClick={() => {
              if (!moveReady) return;
              runMove();
            }}
          >
            {t("richTextEditor.contextMenu.moveSelectionToPage")}
          </button>
        ) : null}
        {showCreate ? (
          <button
            type="button"
            role="menuitem"
            className="promang-rich-text-context-menu__item"
            disabled={!createReady}
            title={
              createReady ? undefined : t("richTextEditor.contextMenu.handlerMissing")
            }
            onClick={() => {
              if (!createReady) return;
              runCreate();
            }}
          >
            {t("richTextEditor.contextMenu.createPageWithSelection")}
          </button>
        ) : null}
      </div>,
      document.body
    );
  };

  return (
    <div
      ref={editorShellRef}
      className={TWclassNames(
        "work-journal-quill",
        fullscreen && "work-journal-quill--fullscreen",
        className
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="promang-rich-text-context-menu__file-input"
        aria-hidden
        tabIndex={-1}
        onChange={onImageFileChange}
      />
      {tablesEnabled ? (
        <div className="work-journal-quill__table-tools">
          <button
            type="button"
            className="work-journal-quill__table-button"
            onClick={insertTable}
          >
            {t("richTextEditor.table.insert")}
          </button>
          <span className="work-journal-quill__table-hint">
            {t("richTextEditor.table.hint")}
          </span>
        </div>
      ) : null}
      <ReactQuill
        ref={quillRef}
        theme="bubble"
        bounds={typeof document !== "undefined" ? document.body : undefined}
        value={value || ""}
        onChange={onChange}
        modules={modules}
        formats={FORMATS}
        placeholder={placeholder}
      />
      {renderContextMenuPortal()}
      <WorkJournalImagePopover
        quill={imagePopover?.quill ?? null}
        imgEl={imagePopover?.img ?? null}
        open={Boolean(imagePopover)}
        anchorPosition={imagePopover?.pos ?? null}
        onClose={closeImagePopover}
        onApplied={() => {
          const q = quillRef.current?.getEditor?.();
          if (q?.root) onChange(q.root.innerHTML);
        }}
      />
    </div>
  );
}
